import { useState, useEffect, useCallback } from 'react';
import { getPendingTransactionCount, getPendingTransactions, forceSyncTransactions } from './offlineStorage';
import { useOnlineStatus } from './pwa';

// Custom hook for offline sync management
export function useOfflineSync() {
  const isOnline = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    const count = await getPendingTransactionCount();
    setPendingCount(count);
  }, []);

  // Initial load and setup listeners
  useEffect(() => {
    updatePendingCount();

    // Listen for service worker messages
    const handleMessage = (event) => {
      if (event.data.type === 'TRANSACTION_QUEUED') {
        updatePendingCount();
      }
      
      if (event.data.type === 'SYNC_COMPLETE') {
        setIsSyncing(false);
        setLastSyncResult(event.data.data);
        updatePendingCount();
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, [updatePendingCount]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && pendingCount > 0) {
      triggerSync();
    }
  }, [isOnline, pendingCount]);

  // Trigger manual sync
  const triggerSync = useCallback(async () => {
    if (!isOnline || pendingCount === 0) return;
    
    setIsSyncing(true);
    forceSyncTransactions();
    
    // Timeout in case SW doesn't respond
    setTimeout(() => {
      setIsSyncing(false);
      updatePendingCount();
    }, 30000);
  }, [isOnline, pendingCount, updatePendingCount]);

  // Get pending transactions for display
  const getPending = useCallback(async () => {
    return await getPendingTransactions();
  }, []);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncResult,
    triggerSync,
    getPending,
    updatePendingCount
  };
}

// Hook to provide auth token to service worker
export function useServiceWorkerAuth(token) {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event) => {
      if (event.data.type === 'GET_AUTH_TOKEN' && event.ports[0]) {
        // Try to get token from cookie or localStorage
        const sessionToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('session_token='))
          ?.split('=')[1];
        
        event.ports[0].postMessage({ token: sessionToken || token || null });
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [token]);
}

// Hook to handle navigation requests from service worker
export function useServiceWorkerNavigation(navigate) {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event) => {
      if (event.data.type === 'NAVIGATE' && event.data.url) {
        navigate(event.data.url);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [navigate]);
}
